import { ENTER, hasModifierKey, SPACE } from '@angular/cdk/keycodes';
import { Directive, HostListener, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { GoogleRecaptcha } from './google-recaptcha.namespace';
import { GoogleRecaptchaService } from './google-recaptcha.service';
import { Status } from '@df/core/status';

@Directive({
    selector: '[googleRecaptcha]',
    standalone: true
})
export class GoogleRecaptchaDirective implements OnInit, OnDestroy {
    @Input()
    googleRecaptchaData?: GoogleRecaptcha.IData;

    @Input()
    googleRecaptcha?: () => void;

    @Input()
    googleRecaptchaAction?: string;

    @Input()
    googleRecaptchaStatus?: Status;

    /**
     * If recaptcha script is loaded
     */
    protected initiated = false;

    /**
     * Callbacks to call on init, used when we try to set captcha without script loaded yet
     */
    protected onReadyCallbacks: GoogleRecaptcha.IOnReadyCallback[] = [];

    protected googleRecaptchaService = inject(GoogleRecaptchaService);

    ngOnInit(): void {
        this.googleRecaptchaService.load().then(() => this.onScriptLoaded());
    }

    ngOnDestroy(): void {
        this.googleRecaptchaService.hideBadge(this);
    }

    /**
     * When script is loaded bind service to directive and mark as loaded
     * call on init
     */
    private onScriptLoaded() {
        if (!this.initiated && this.googleRecaptchaService.grecaptcha) {
            this.initiated = true;
            this.googleRecaptchaService.displayBadge(this);
        }
    }

    @HostListener('click')
    onClick(): void {
        this.ready();
    }

    @HostListener('keydown', ['$event'])
    handleKeydown(event: KeyboardEvent): void {
        const keyCode = event.keyCode;
        const isOpenKey = keyCode === ENTER || keyCode === SPACE;

        if (isOpenKey && !hasModifierKey(event)) {
            this.ready();
        }
    }

    private ready(): void {
        const onReady = (token: string) => {
            if (this.googleRecaptchaData && this.googleRecaptcha) {
                this.googleRecaptchaData.token = token;
                this.googleRecaptcha();
            }
        };
        this.onReadyCallbacks.push(onReady);

        if (this.initiated) {
            this.execute();
        }
    }

    private execute() {
        if (this.googleRecaptchaStatus) {
            this.googleRecaptchaStatus.busy = true;
        }
        const unbusyStatus = () => {
            if (this.googleRecaptchaStatus) {
                this.googleRecaptchaStatus.busy = false;
            }
        };

        this.googleRecaptchaService
            .execute(this.googleRecaptchaAction)
            .then(token => {
                unbusyStatus();
                let action: GoogleRecaptcha.IOnReadyCallback | undefined;

                while ((action = this.onReadyCallbacks.shift())) {
                    action(token);
                }
            })
            .catch(unbusyStatus);
    }
}
